import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '🌍',
  number: 'Tomo 1',
  title: 'Fundamentos de la Producción Agropecuaria',
  subtitle: 'El punto de partida para enseñar producción agropecuaria desde cero.',
  problem: 'El primer contacto con la producción agropecuaria puede ser el más transformador o el más abstracto. Este tomo convierte un tema técnico en algo comprensible y conectado con la realidad del alumno desde el primer día.',
  bullets: ['Introducción a la actividad agropecuaria e importancia económica por país', 'Sistemas productivos y zonas agroecológicas de América Latina', 'Cadena productiva y tipos de productores', 'Establecimiento escolar agropecuario', 'Suelo como recurso productivo, fertilización y conservación', 'Compost escolar'],
  result: 'Tus alumnos entienden la producción agropecuaria como un sistema lógico y conectado con su territorio, no como un conjunto de términos técnicos imposibles de relacionar.'
}, {
  icon: '🌾',
  number: 'Tomo 2',
  title: 'Producción Vegetal y Ganadería Bovina',
  subtitle: 'Un tomo clave para trabajar los principales cultivos y la ganadería bovina de América Latina.',
  problem: 'Este es el corazón productivo del año. Tus alumnos trabajarán los cultivos de su región y entenderán la ganadería bovina con material claro, técnico y adaptado al contexto latinoamericano.',
  bullets: ['Clasificación de cultivos: extensivos, tropicales, subtropicales y andinos', 'Ciclo del cultivo y manejo de plagas y enfermedades', 'Calendario agrícola por país', 'Razas bovinas y sistemas de producción', 'Alimentación, sanidad animal, instalaciones y registros ganaderos'],
  result: 'Tus alumnos aprenden a identificar, manejar y planificar los cultivos y la ganadería más relevantes de su zona, con criterio técnico y visión productiva.'
}, {
  icon: '💧',
  number: 'Tomo 3',
  title: 'Riego, Maquinaria y Seguridad Rural',
  subtitle: 'Material enfocado en el uso del agua, los sistemas de riego, las herramientas y la seguridad en el trabajo agropecuario.',
  problem: 'Uno de los puntos más prácticos del año. Tus alumnos entenderán cómo funciona el agua en la producción y aprenderán a operar maquinaria y herramientas de forma segura.',
  bullets: ['Manejo del agua y necesidades hídricas de los cultivos', 'Riego por gravedad, aspersión y goteo', 'Captación, almacenamiento y eficiencia hídrica', 'Herramientas, tractor e implementos agrícolas', 'Mantenimiento y prevención de accidentes rurales'],
  result: 'Tus alumnos comprenden cómo gestionar el agua y la maquinaria en forma eficiente y segura, aplicando criterio técnico en situaciones reales del campo.'
}, {
  icon: '♻️',
  number: 'Tomo 4',
  title: 'Sustentabilidad y Producción Animal',
  subtitle: 'Un tomo actual para enseñar producción responsable, agroecología y producción animal.',
  problem: 'La producción del futuro requiere mirada ambiental. Este tomo conecta la realidad productiva con la sustentabilidad, la agroecología y el manejo responsable de animales.',
  bullets: ['Producción sustentable, agroecología y sistemas agroforestales', 'Agricultura orgánica, cambio climático y energías renovables', 'Avicultura y producción porcina', 'Alimentación, manejo y sanidad animal'],
  result: 'Tus alumnos integran la visión ambiental y productiva, entendiendo que producir bien y cuidar el territorio no son objetivos opuestos.',
  popular: true
}, {
  icon: '🏪',
  number: 'Tomo 5',
  title: 'Del Campo al Mercado',
  subtitle: 'El cierre del recorrido: producir, cuidar la calidad, vender y transformar el aprendizaje en un proyecto real.',
  problem: 'Muchos alumnos aprenden a producir pero no saben qué hacer con lo que producen. Este tomo les enseña a cerrar el ciclo completo con claridad económica y visión comercial.',
  bullets: ['Poscosecha, manejo de productos frescos, clasificación y empaque', 'Almacenamiento, conservación y agregado de valor', 'Comercialización, economía rural, costos y rentabilidad', 'Cooperativismo y asociatividad rural', 'Proyecto Final Productivo 360°'],
  result: 'Tus alumnos cierran el año con un proyecto productivo real, con criterio técnico, económico y comercial. Saben producir, cuidar y vender.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-cream" data-id="element-296">
      <div className="max-w-6xl mx-auto" data-id="element-297">
        <div className="text-center mb-16" data-id="element-298">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-slate-900" data-id="element-299">
            📘 Los 5 tomos del Mega Pack
          </h2>
          <p className="text-xl text-earth" data-id="element-300">Material completo para el aula.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-301">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-white rounded-2xl border ${tomo.popular ? 'border-forest shadow-[0_0_30px_rgba(30,58,36,0.15)]' : 'border-earth/20'} p-8 relative overflow-hidden flex flex-col h-full shadow-sm`} data-id="element-302">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-forest text-cream font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-303">
                  EL MÁS ACTUAL
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-304">
                <span className="text-4xl" data-id="element-305">{tomo.icon}</span>
                <div data-id="element-306">
                  <div className="text-forest font-bold text-sm uppercase tracking-wider" data-id="element-307">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1 text-slate-900" data-id="element-308">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-slate-700 font-medium mb-6 italic border-l-2 border-forest/50 pl-4" data-id="element-309">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-310">
                <h4 className="text-sm font-bold text-earth uppercase tracking-wider mb-2" data-id="element-311">
                  ¿Qué resuelve en el aula?
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-312">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-313">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-314">
                    <CheckCircle2 size={18} className="text-forest shrink-0 mt-0.5" data-id="element-315" />
                    <span className="text-slate-700 text-sm" data-id="element-316">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-forest/5 border border-forest/20 rounded-xl p-4 mt-auto" data-id="element-317">
                <span className="text-forest font-bold block mb-1" data-id="element-318">
                  Resultado:
                </span>
                <p className="text-slate-800 text-sm" data-id="element-319">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}